// Readiness Utilities
// Standardized readiness percentage ranges and colors

// Readiness level ranges
// 90+: very high (dark green)
// 66-90: high (light green)
// 36-65: medium (yellow)
// 16-35: low (red)
// 0-15: critical (dark red)

/**
 * Readiness level constants
 */
export const READINESS_LEVELS = {
  VERY_HIGH: { min: 90, max: 100, label: "very-high", color: "#059669" },
  HIGH: { min: 66, max: 89, label: "high", color: "#10b981" },
  MEDIUM: { min: 36, max: 65, label: "medium", color: "#fbbf24" },
  LOW: { min: 16, max: 35, label: "low", color: "#ef4444" },
  CRITICAL: { min: 0, max: 15, label: "critical", color: "#991b1b" },
};

/**
 * Gets the readiness level label from a percentage
 * @param {number} readiness - Readiness percentage (0-100)
 * @returns {string} Readiness level: 'very-high', 'high', 'medium', 'low', or 'critical'
 */
export const getReadinessLevel = (readiness) => {
  if (typeof readiness !== "number") return "critical";
  
  if (readiness >= READINESS_LEVELS.VERY_HIGH.min) return READINESS_LEVELS.VERY_HIGH.label;
  if (readiness >= READINESS_LEVELS.HIGH.min) return READINESS_LEVELS.HIGH.label;
  if (readiness >= READINESS_LEVELS.MEDIUM.min) return READINESS_LEVELS.MEDIUM.label;
  if (readiness >= READINESS_LEVELS.LOW.min) return READINESS_LEVELS.LOW.label;
  return READINESS_LEVELS.CRITICAL.label;
};

/**
 * Gets the color for a readiness percentage
 * Standardized colors: very-high (#059669), high (#10b981), medium (#fbbf24), low (#ef4444), critical (#991b1b)
 * @param {number} readiness - Readiness percentage (0-100)
 * @returns {string} Hex color code
 */
export const getReadinessColor = (readiness) => {
  if (typeof readiness !== "number") return "#991b1b"; // default to critical color
  
  if (readiness >= READINESS_LEVELS.VERY_HIGH.min) return READINESS_LEVELS.VERY_HIGH.color;
  if (readiness >= READINESS_LEVELS.HIGH.min) return READINESS_LEVELS.HIGH.color;
  if (readiness >= READINESS_LEVELS.MEDIUM.min) return READINESS_LEVELS.MEDIUM.color;
  if (readiness >= READINESS_LEVELS.LOW.min) return READINESS_LEVELS.LOW.color;
  return READINESS_LEVELS.CRITICAL.color;
};

/**
 * Readiness gradients for CSS classes (horizontal gradients for bars)
 */
export const readinessGradients = {
  "very-high": "linear-gradient(90deg, #059669, #047857)",
  high: "linear-gradient(90deg, #10b981, #059669)",
  medium: "linear-gradient(90deg, #fbbf24, #f59e0b)",
  low: "linear-gradient(90deg, #ef4444, #dc2626)",
  critical: "linear-gradient(90deg, #991b1b, #7f1d1d)",
};

/**
 * Gets the gradient for a readiness percentage
 * @param {number} readiness - Readiness percentage (0-100)
 * @returns {string} CSS linear-gradient string
 */
export const getReadinessGradient = (readiness) => {
  const level = getReadinessLevel(readiness);
  return readinessGradients[level] || readinessGradients.critical;
};

/**
 * Readiness level priority map (lower number = higher priority/severity)
 * Used for sorting (critical issues first)
 */
export const readinessLevelPriority = {
  critical: 0,
  low: 1,
  medium: 2,
  high: 3,
  "very-high": 4,
};

/**
 * Gets priority value for a readiness level (for sorting - lower readiness = higher priority)
 * @param {number} readiness - Readiness percentage (0-100)
 * @returns {number} Priority value (lower = higher priority/severity)
 */
export const getReadinessPriority = (readiness) => {
  const level = getReadinessLevel(readiness);
  return readinessLevelPriority[level] ?? 99;
};

/**
 * Formats readiness percentage for display
 * @param {number} readiness - Readiness percentage
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted string
 */
export const formatReadiness = (readiness, decimals = 1) => {
  if (typeof readiness !== "number") return "0.0";
  return readiness.toFixed(decimals);
};
